import { FieldArray, Formik } from 'formik';
import { Form, FormItem, Input, SubmitButton } from 'formik-antd';
import { useEffect, useRef } from 'react';

import { Alert, Divider, Row } from '$HVUI';
import { useFetch } from '$Hooks';
import Button from '$Molecules/Buttons/Button';
import { useTokenViewContext } from '$TokenView/TokenViewContext';
import { adjustCaretAfterEdit, flexibleDateString } from '$Utils';
import {
    formatPhone,
    normalizeDateBasic,
    normalizePhone
} from '$Validation/Normalizer';
import yup from '$Validation/yup';

const { array, date, lazy, mixed, object, string } = yup;

const contactFields = {
    first_name: {
        component: Input,
        label: 'First Name',
        name: 'first_name',
        required: true
    },
    last_name: {
        component: Input,
        label: 'Last Name',
        name: 'last_name',
        required: true
    },
    date_of_contact: {
        component: Input,
        label: 'When did you last have contact with this person?',
        name: 'date_of_contact',
        placeholder: 'MM/DD/YYYY',
        required: true,
        onChange: (e, setFieldValue, instanceName) => {
            let value = e.target.value;
            value = normalizeDateBasic(value);
            setFieldValue(instanceName, value);
            adjustCaretAfterEdit(e.target);
        }
    },
    phone: {
        component: Input,
        label: 'Phone Number',
        name: 'phone',
        placeholder: '(555) 555-5555',
        type: 'tel',
        onChange: (e, setFieldValue, instanceName) => {
            let value = e.target.value;
            value = formatPhone(value);
            setFieldValue(instanceName, value);
        }
    },
    email: {
        component: Input,
        label: 'Email',
        name: 'email',
        type: 'email'
    }
};

const schema = object().shape({
    contacts: array().of(
        object().shape({
            first_name: string()
                .label(contactFields['first_name'].label)
                .required('Required'),
            last_name: string()
                .label(contactFields['last_name'].label)
                .required('Required'),
            date_of_contact: date()
                .label('Contact date')
                .required('Required')
                .typeError('Must be a valid date')
                .limitDateRange({ minNum: 14 }),
            phone: lazy(value =>
                mixed()
                    .label(contactFields['phone'].label)
                    .when('email', (email, schema) => {
                        if (!email) {
                            return schema.required(
                                'Either phone or email required'
                            );
                        }
                        return schema.notRequired();
                    })
                    .phone('US')
            ),
            email: lazy(value =>
                string()
                    .label(contactFields['email'].label)
                    .when('phone', (phone, schema) => {
                        if (!phone)
                            return schema.required(
                                'Either phone or email required'
                            );
                        return schema.notRequired();
                    })
                    .email()
            )
        })
    )
});

// render for one set of Contact Fields
const ContactFieldSet = props => {
    const { arrayHelpers, index, setFieldValue } = props;

    return (
        <div id={`contact-${index}-wrapper`} key={index}>
            {!!index && (
                <>
                    <Divider />
                    <Row style={{ justifyContent: 'flex-end' }}>
                        <Button
                            kind="primary-narrow"
                            text="Remove Contact"
                            onClick={() => {
                                arrayHelpers.remove(index);
                            }}
                        />
                    </Row>
                </>
            )}
            <h3
                style={{
                    marginBottom: 36
                }}
            >
                Contact {index + 1}
            </h3>
            {Object.values(contactFields).map(field => {
                const {
                    component: Component,
                    label,
                    name,
                    onChange,
                    placeholder,
                    required
                } = field;

                // Using FieldArray, we can have an array values nested
                // in Formik's values object.

                // This instanceName variable shows the format we use to refer to them.
                // See https://formik.org/docs/api/fieldarray#fieldarray-array-of-objects
                // for more details.
                const instanceName = `contacts.${index}.${name}`;

                return (
                    <FormItem
                        htmlFor={instanceName}
                        key={instanceName}
                        label={label}
                        layout="vertical"
                        name={instanceName}
                        required={required}
                        style={{
                            alignSelf: 'flex-start',
                            marginBottom: '1em'
                        }}
                    >
                        <Component
                            id={instanceName}
                            name={instanceName}
                            onChange={
                                onChange
                                    ? e => {
                                          onChange(
                                              e,
                                              setFieldValue,
                                              instanceName
                                          );
                                      }
                                    : undefined
                            }
                            placeholder={placeholder}
                            size="large"
                        />
                    </FormItem>
                );
            })}
        </div>
    );
};

const ContactTracingForm = props => {
    const {
        contactTraceSubmittedThisSession,
        setContactTraceSubmittedThisSession
    } = useTokenViewContext();
    const { _initialValues = { contacts: [{}] }, token } = props;

    const contactTraceTop = useRef();
    let serverErrorMessage;

    const {
        error,
        post,
        loading,
        response: contactTraceResponse
    } = useFetch(`/api/v2/contact-tracing/intake/${token}/`);

    const handleContactTraceSubmit = async values => {
        const { contacts } = values;
        const finalContacts = contacts.map(
            ({ date_of_contact, email, first_name, last_name, phone }) => {
                return {
                    date_of_contact: flexibleDateString(
                        date_of_contact,
                        null,
                        'ISO'
                    ),
                    email,
                    first_name,
                    last_name,
                    phone: normalizePhone(phone).replaceAll(' ', '')
                };
            }
        );
        await post(null, finalContacts);
    };

    // Focus on the error
    useEffect(() => {
        if (loading) return;
        if (error && !!serverErrorMessage) serverErrorMessage.focus();
        if (contactTraceResponse.ok) {
            setContactTraceSubmittedThisSession(true);
            contactTraceTop?.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [error, loading, contactTraceResponse]);

    return (
        <div id="contact-tracing-top" ref={contactTraceTop}>
            {!contactTraceSubmittedThisSession && (
                <>
                    <Divider />
                    <h2 style={{ textAlign: 'center', marginBottom: '24px' }}>
                        Help Save Lives
                    </h2>
                    <p>
                        Anonymously notify anyone with whom you've been in close
                        contact (within 6 feet for 15 minutes or more in a
                        24-hour period). Notifying your contacts may help them
                        prevent spread of the infection. We’ll immediately send
                        them information on how to get a free COVID-19 test and
                        instructions on how to protect themselves and their
                        loved ones.
                    </p>
                    <p>
                        <strong>
                            Please enter the names and contact information for
                            anyone you’ve been in close contact with beginning 2
                            days before the appointment date above.
                        </strong>
                    </p>
                    <Formik
                        initialValues={_initialValues}
                        onSubmit={handleContactTraceSubmit}
                        validationSchema={schema}
                    >
                        {formikProps => {
                            const { values } = formikProps;
                            return (
                                <Form
                                    layout="vertical"
                                    style={{ justifyContent: 'start' }}
                                    // TODO - this `requiredMark` didn't seem to be doing anything. okay to delete?
                                    // requiredMark={true}
                                >
                                    <FieldArray name="contacts">
                                        {arrayHelpers => {
                                            return (
                                                <>
                                                    {values.contacts.map(
                                                        (contact, index) => (
                                                            <ContactFieldSet
                                                                {...formikProps}
                                                                index={index}
                                                                arrayHelpers={
                                                                    arrayHelpers
                                                                }
                                                                key={index}
                                                            />
                                                        )
                                                    )}
                                                    <Row
                                                        style={{
                                                            justifyContent:
                                                                'center',
                                                            marginBottom: 36
                                                        }}
                                                    >
                                                        <Button
                                                            id="add-new-contact"
                                                            kind="secondary"
                                                            text="Add Another Contact"
                                                            onClick={() => {
                                                                // Push a new field with no initial state;
                                                                // Prevents premature validation
                                                                arrayHelpers.push(
                                                                    {}
                                                                );
                                                            }}
                                                        />
                                                    </Row>
                                                </>
                                            );
                                        }}
                                    </FieldArray>

                                    <Row style={{ justifyContent: 'center' }}>
                                        <div
                                            ref={node => {
                                                serverErrorMessage = node;
                                            }}
                                            id="submit-error"
                                        >
                                            {error && (
                                                <Alert
                                                    type="error"
                                                    showIcon
                                                    message="Sorry, something went wrong submitting the form. Try again later."
                                                    style={{
                                                        marginBottom: '24px'
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <SubmitButton
                                            size="large"
                                            type="primary"
                                        >
                                            Notify Contacts
                                        </SubmitButton>
                                    </Row>
                                </Form>
                            );
                        }}
                    </Formik>
                </>
            )}
            {contactTraceSubmittedThisSession && (
                <Row
                    id="contact-trace-submitted"
                    role="alert"
                    style={{ justifyContent: 'center' }}
                >
                    <Divider />
                    <p>
                        <span style={{ fontWeight: '800' }}>
                            Contact(s) submitted.
                        </span>{' '}
                        Thank you for slowing the spread of COVID-19!
                    </p>
                    <p>Don't forget to read more about your result below.</p>
                </Row>
            )}
        </div>
    );
};

export default ContactTracingForm;
